import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useThrottledCallback } from 'use-debounce';

import { actions } from '../../state';
import { socketsClient } from '../../api/sockets';

import DeviceUptimeChartsRow from './deviceUptimeChartsRow';
import {
  buildDefaultDeviceStatistics,
  buildDeviceStatistics,
} from './deviceUptimeCharts.utils';

import styles from './deviceUptimeCharts.module.scss';

// device_update fires per-device; fleet traffic would otherwise refetch /summary continuously.
const DEVICE_UPDATE_REFETCH_THROTTLE_MS = 30000;

const DeviceUptimeCharts = ({ dispatch, availabilityDevices }) => {
  const clearAvailabilityDevices = useCallback(() => {
    dispatch(actions.monitoring.clearAvailabilityDevices());
  }, [dispatch]);

  const fetchAvailabilityDevicesData = useCallback(() => {
    dispatch(actions.monitoring.getAvailabilityDevices());
  }, [dispatch]);

  const throttledFetchOnDeviceUpdate = useThrottledCallback(
    fetchAvailabilityDevicesData,
    DEVICE_UPDATE_REFETCH_THROTTLE_MS,
    { leading: true, trailing: true }
  );

  useEffect(() => () => clearAvailabilityDevices(), [clearAvailabilityDevices]);

  useEffect(() => {
    fetchAvailabilityDevicesData();
  }, [fetchAvailabilityDevicesData]);

  useEffect(() => {
    if (!socketsClient) {
      return undefined;
    }

    const channel = socketsClient.subscribe('device_update');
    channel.watch(() => {
      throttledFetchOnDeviceUpdate();
    });

    return () => {
      throttledFetchOnDeviceUpdate.cancel();
      socketsClient.unsubscribe('device_update');
    };
  }, [throttledFetchOnDeviceUpdate]);

  const deviceStatistics = useMemo(() => {
    const statistics = buildDeviceStatistics(availabilityDevices);

    return statistics || buildDefaultDeviceStatistics();
  }, [availabilityDevices]);

  return (
    <section className={styles.deviceChartsSection}>
      <h2 className={styles.deviceChartsSectionTitle}>Dabartinė būsena</h2>
      <div className={styles.deviceChartsWrapper}>
        <DeviceUptimeChartsRow deviceStatistics={deviceStatistics} />
      </div>
    </section>
  );
};

const mapStateToProps = state => ({
  availabilityDevices: state.monitoring.availabilityDevices,
});

DeviceUptimeCharts.propTypes = {
  dispatch: PropTypes.func.isRequired,
  availabilityDevices: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.shape({
      http_device_count: PropTypes.number,
      http_faulty_device_count: PropTypes.number,
      http_active_device_count: PropTypes.number,
      http_faulty_device_ids: PropTypes.arrayOf(PropTypes.number),
      mqtt_device_count: PropTypes.number,
      mqtt_faulty_device_count: PropTypes.number,
      mqtt_active_device_count: PropTypes.number,
      mqtt_faulty_device_ids: PropTypes.arrayOf(PropTypes.number),
    }),
  ]),
};

export default connect(mapStateToProps)(DeviceUptimeCharts);
